import React, { useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import apiClient from '../../stateManagers/services/axiosInterceptor';

interface CsvTeacher {
  name: string;
  qualification: string;
  gender: string;
  marital_status: string;
  email_id: string;
  parentage: string;
  dob: string;
  address: string;
  contact_no: string;
  user_login: string;
  user_pass: string;
  account_no?: string;
  bank_name?: string;
  ifsc_code?: string;
}

interface CsvPreviewGroup {
  marital_status: string;
  teachers: CsvTeacher[];
}

interface ValidationError {
  row: number;
  message: string;
  details?: string[];
  data: {
    name: string;
    email: string;
  };
}

interface ValidationResult {
  status: 'error';
  message: string;
  validation_results: {
    valid_count: number;
    invalid_count: number;
    errors: ValidationError[];
  };
}

interface SuccessResult {
  status: 'success';
  message: string;
  process_results: {
    success: number;
    failed: number;
    successful_teachers: Array<{
      name: string;
      email: string;
      teacher_id: number;
      user_id: number;
      username: string;
      password: string;
    }>;
  };
}

type ApiResponse = ValidationResult | SuccessResult;

export default function TeacherBulkUpload() {
  const [uploading, setUploading] = useState(false);
  const [csvPreview, setCsvPreview] = useState<CsvPreviewGroup[]>([]);
  const [fileSelected, setFileSelected] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [successResults, setSuccessResults] = useState<SuccessResult | null>(null);
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const styles = `
    @keyframes fadeInSlide {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .p-fileupload .p-button {
      background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important;
      border: none !important;
      border-radius: 12px !important;
      padding: 10px 20px !important;
      font-size: 14px !important;
      font-weight: 600 !important;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3) !important;
    }
    .p-fileupload .p-button:hover {
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4) !important;
    }
    .p-fileupload-content {
      background: #f9fafb !important;
      border: 2px dashed #e5e7eb !important;
      border-radius: 12px !important;
      padding: 20px !important;
    }
    .error-row {
      background: #fef2f2 !important;
      border-left: 4px solid #ef4444 !important;
    }
  `;

  const handleFileSelect = (event: any) => {
    const file = event.files[0];
    if (!file) {
      setFileSelected(false);
      return;
    }

    setFileSelected(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCsvPreview(text);
    };
    reader.readAsText(file);
  };

  const parseCsvLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const parseCsvPreview = (text: string) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length < 2) {
      toast.current?.show({
        severity: 'error',
        summary: 'Invalid CSV',
        detail: 'CSV must have header and at least one data row',
        life: 3000,
      });
      return;
    }

    const headers = parseCsvLine(lines[0]).map(h => h.toLowerCase().trim());
    
    console.log('CSV Headers found:', headers);

    const columnMap: { [key: string]: string } = {
      'contact_n': 'contact_no',
      'marital_statu': 'marital_status',
      'account_n': 'account_no',
      'bank_nam': 'bank_name',
      'ifsc_cod': 'ifsc_code',
    };

    const normalizedHeaders = headers.map(h => {
      let cleaned = h.replace(/[._]+$/, '');
      return columnMap[cleaned] || cleaned;
    });

    console.log('Normalized headers:', normalizedHeaders);

    const required = ['name', 'qualification', 'gender', 'marital_status', 'email_id', 'parentage', 'dob', 'address', 'contact_no', 'user_login', 'user_pass'];
    const missing = required.filter(h => !normalizedHeaders.includes(h));
    
    if (missing.length > 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Missing Columns',
        detail: `Required: ${missing.join(', ')}. Found: ${normalizedHeaders.join(', ')}`,
        life: 6000,
      });
      console.error('Missing columns:', missing);
      console.error('Found columns:', normalizedHeaders);
      return;
    }

    const maritalStatusMap: { [key: string]: CsvPreviewGroup } = {};
    let successfulRows = 0;
    let skippedRows = 0;

    for (let i = 1; i < lines.length; i++) {
      const values = parseCsvLine(lines[i]).map(v => v.replace(/^"|"$/g, ''));
      
      if (values.length < normalizedHeaders.length) {
        console.warn(`Row ${i + 1} has ${values.length} columns, expected ${normalizedHeaders.length}. Skipping.`);
        skippedRows++;
        continue;
      }

      const row: any = {};
      normalizedHeaders.forEach((h, idx) => {
        row[h] = values[idx] || '';
      });

      if (!row.name || !row.email_id) {
        console.warn(`Row ${i + 1} missing essential data. Skipping.`);
        skippedRows++;
        continue;
      }

      const key = row.marital_status || 'Not Specified';
      if (!maritalStatusMap[key]) {
        maritalStatusMap[key] = {
          marital_status: key,
          teachers: []
        };
      }

      maritalStatusMap[key].teachers.push({
        name: row.name || '',
        qualification: row.qualification || '',
        gender: row.gender || '',
        marital_status: row.marital_status || '',
        email_id: row.email_id || '',
        parentage: row.parentage || '',
        dob: row.dob || '',
        address: row.address || '',
        contact_no: row.contact_no || '',
        user_login: row.user_login || 'DEFAULT',
        user_pass: row.user_pass || 'DEFAULT',
        account_no: row.account_no || '',
        bank_name: row.bank_name || '',
        ifsc_code: row.ifsc_code || '',
      });
      
      successfulRows++;
    }

    const preview = Object.values(maritalStatusMap);
    setCsvPreview(preview);

    const total = preview.reduce((sum, g) => sum + g.teachers.length, 0);
    
    if (skippedRows > 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Preview Ready with Warnings',
        detail: `${preview.length} group(s), ${total} teacher(s) parsed. ${skippedRows} row(s) skipped due to errors.`,
        life: 5000,
      });
    } else {
      toast.current?.show({
        severity: 'info',
        summary: 'Preview Ready',
        detail: `${preview.length} group(s), ${total} teacher(s)`,
        life: 3000,
      });
    }
  };

  const handleFileUpload = async (event: any) => {
    const file = event.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('csv_file', file);

    try {
      setUploading(true);
      const response = await apiClient.post<ApiResponse>('/register-teachers-csv', formData);

      if (response.data.status === 'error') {
        const result = response.data as ValidationResult;
        setValidationErrors(result.validation_results.errors);
        setShowErrors(true);
        setSuccessResults(null);
        toast.current?.show({
          severity: 'error',
          summary: 'Validation Failed',
          detail: `${result.validation_results.invalid_count} row(s) have errors`,
          life: 4000,
        });
      } else {
        const result = response.data as SuccessResult;
        setSuccessResults(result);
        toast.current?.show({
          severity: 'success',
          summary: 'Upload Complete',
          detail: `${result.process_results.success} teachers registered!`,
          life: 4000,
        });

        setCsvPreview([]);
        setFileSelected(false);
        if (fileUploadRef.current) fileUploadRef.current.clear();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Upload Failed',
        detail: error.response?.data?.message || 'Server error',
        life: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const headers = ['name', 'qualification', 'gender', 'marital_status', 'email_id', 'parentage', 'dob', 'address', 'contact_no', 'user_login', 'user_pass', 'account_no', 'bank_name', 'ifsc_code'];
    const sample = [
      headers.join(','),
      'Dr. Sarah Johnson,PhD in Mathematics,Female,Married,sarah.johnson@school.com,D/O Robert Johnson,1980-03-10,"123 Main St, City",9876543210,sarah.johnson,Pass123!,ACC123456,State Bank,SBIN0001234',
      'Mr. James Smith,MSc Physics,Male,Single,DEFAULT,S/O David Smith,1985-05-20,"456 Oak Ave, City",9876543220,DEFAULT,DEFAULT,,,',
      'Mrs. Emily Brown,MA English,Female,Married,emily.brown@school.com,D/O Michael Brown,1982-07-15,"789 Pine Rd, City",9876543230,emily.brown,SecurePass1,,,'
    ].join('\n');

    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_teachers.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalTeachers = csvPreview.reduce((sum, g) => sum + g.teachers.length, 0);

  return (
    <>
      <style>{styles}</style>
      <Toast ref={toast} />

      <div style={{ minHeight: '100vh', padding: '0px', background: '#fdfdfdff' }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #2855ae 0%, #3b82f6 100%)',
          borderRadius: '24px',
          padding: '32px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '16px',
              padding: '12px',
            }}>
              <i className="pi pi-id-card" style={{ fontSize: '18px', color: 'white' }}></i>
            </div>
            <div>
              <h1 style={{ color: 'white', fontSize: '28px', fontWeight: 600, margin: '0 0 4px 0' }}>
                Teacher Bulk Registration
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{
                  background: 'rgba(74,222,128,0.9)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600
                }}>
                  {totalTeachers} Teachers
                </span>
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px' }}>
                  {new Date().toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Card */}
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '28px',
          marginBottom: '24px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '16px',
              padding: '12px',
            }}>
              <i className="pi pi-upload" style={{ fontSize: '18px', color: 'white' }}></i>
            </div>
            <div>
              <h3 style={{ color: '#2855ae', fontSize: '18px', fontWeight: 600, margin: '0 0 4px 0' }}>
                Upload Teacher CSV
              </h3>
              <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>
                Register multiple teachers at once using CSV file
              </p>
            </div>
          </div>

          <FileUpload
            ref={fileUploadRef}
            name="csv_file"
            accept=".csv"
            maxFileSize={10000000}
            customUpload
            uploadHandler={handleFileUpload}
            onSelect={handleFileSelect}
            onClear={() => { setFileSelected(false); setCsvPreview([]); }}
            onRemove={() => { setFileSelected(false); setCsvPreview([]); }}
            disabled={uploading}
            chooseLabel="Choose CSV"
            uploadLabel="Register Teachers"
            cancelLabel="Clear"
            emptyTemplate={<p style={{ color: '#6b7280', textAlign: 'center', margin: 0 }}>Drag and drop CSV file here</p>}
          />

          {uploading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '20px' }}>
              <ProgressSpinner style={{ width: '40px', height: '40px' }} />
              <span style={{ marginLeft: '12px', color: '#2855ae', fontWeight: 500 }}>Processing teachers...</span>
            </div>
          )}
        </div>

        {/* Instructions */}
        {csvPreview.length === 0 && !successResults && (
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            border: '2px solid #dbeafe',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                borderRadius: '10px',
                padding: '8px',
                minWidth: '32px',
                height: '32px',
              }}>
                <i className="pi pi-info-circle" style={{ fontSize: '14px', color: 'white' }}></i>
              </div>
              <div style={{ flex: 1 }}>
                <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: 600, margin: '0 0 8px 0' }}>
                  CSV Format Requirements
                </h4>
                <div style={{ color: '#6b7280', fontSize: '13px', lineHeight: '1.6' }}>
                  <p style={{ marginBottom: '8px' }}><strong>Required columns:</strong></p>
                  <ul style={{ marginTop: '4px', marginBottom: '12px', paddingLeft: '20px' }}>
                    <li>name, qualification, gender, marital_status</li>
                    <li>email_id, parentage, dob, address</li>
                    <li>contact_no, user_login, user_pass</li>
                  </ul>
                  <p style={{ marginBottom: '4px' }}><strong>Optional columns:</strong> account_no, bank_name, ifsc_code</p>
                  <p style={{ marginBottom: '4px' }}><strong>Date format:</strong> YYYY-MM-DD or DD-MM-YYYY</p>
                  <p style={{ marginBottom: '4px' }}><strong>Parentage format:</strong> S/O (Son of), D/O (Daughter of), W/O (Wife of)</p>
                  <p style={{ marginBottom: '4px' }}><strong>Gender:</strong> Male, Female, Other</p>
                  <p style={{ marginBottom: '4px' }}><strong>DEFAULT values:</strong> Use "DEFAULT" for auto-generated username/password</p>
                  <p style={{ marginBottom: '4px' }}><strong>Password requirements:</strong> Minimum 8 characters, at least one uppercase letter and one number</p>
                  <p style={{ marginTop: '12px', padding: '8px', background: '#fef3c7', borderRadius: '6px', fontSize: '12px' }}>
                    <strong>Note:</strong> When username/password is set to DEFAULT, they will be auto-generated based on the teacher's name and date of birth.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <Button
                onClick={downloadSampleCSV}
                icon="pi pi-download"
                label="Download Sample CSV"
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '10px 20px',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              />
            </div>
          </div>
        )}

        {/* CSV Preview */}
        {csvPreview.length > 0 && (
          <div style={{
            background: '#f9fafb',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #e5e7eb',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px',
            }}>
              <h4 style={{
                color: '#2855ae',
                fontSize: '16px',
                fontWeight: 600,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <i className="pi pi-eye"></i>
                CSV Preview ({csvPreview.length} group(s), {totalTeachers} teachers)
              </h4>
              <Button
                icon="pi pi-times"
                label="Clear"
                onClick={() => {
                  setCsvPreview([]);
                  setFileSelected(false);
                  fileUploadRef.current?.clear();
                }}
                style={{
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '6px 12px',
                  fontSize: '12px',
                }}
              />
            </div>

            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {csvPreview.map((group, gIdx) => (
                <div key={gIdx} style={{ marginBottom: '20px' }}>
                  <h5 style={{
                    color: '#2855ae',
                    fontSize: '14px',
                    fontWeight: 600,
                    margin: '0 0 8px 0',
                    paddingBottom: '4px',
                    borderBottom: '1px solid #e5e7eb',
                  }}>
                    {group.marital_status} ({group.teachers.length} teachers)
                  </h5>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {group.teachers.map((teacher, tIdx) => (
                      <div key={tIdx} style={{
                        background: 'white',
                        borderRadius: '8px',
                        padding: '12px',
                        border: '1px solid #e5e7eb',
                        fontSize: '13px',
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                          <div><strong>Name:</strong> {teacher.name}</div>
                          <div><strong>Gender:</strong> {teacher.gender}</div>
                          <div><strong>Qualification:</strong> {teacher.qualification}</div>
                          <div><strong>Email:</strong> {teacher.email_id}</div>
                          <div><strong>Contact:</strong> {teacher.contact_no}</div>
                          <div><strong>DOB:</strong> {teacher.dob}</div>
                          <div><strong>Parentage:</strong> {teacher.parentage}</div>
                          <div><strong>Username:</strong> {teacher.user_login === 'DEFAULT' ? 'Auto' : teacher.user_login}</div>
                          <div><strong>Password:</strong> {teacher.user_pass === 'DEFAULT' ? 'Auto' : '••••••••'}</div>
                        </div>
                        {(teacher.account_no || teacher.bank_name || teacher.ifsc_code) && (
                          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#6b7280' }}>
                            <strong>Banking:</strong> {teacher.account_no && `Acc: ${teacher.account_no}`} {teacher.bank_name && `| ${teacher.bank_name}`} {teacher.ifsc_code && `| ${teacher.ifsc_code}`}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Success Results */}
        {successResults && (
          <div style={{
            background: 'white',
            borderRadius: '20px',
            padding: '28px',
            marginTop: '24px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
            animation: 'fadeInSlide 0.5s ease-out',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                borderRadius: '16px',
                padding: '12px',
              }}>
                <i className="pi pi-check-circle" style={{ fontSize: '18px', color: 'white' }}></i>
              </div>
              <div>
                <h3 style={{ color: '#2855ae', fontSize: '18px', fontWeight: 600, margin: '0 0 4px 0' }}>
                  Registration Successful!
                </h3>
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>
                  {successResults.message}
                </p>
              </div>
              <Button
                icon="pi pi-times"
                onClick={() => setSuccessResults(null)}
                style={{
                  marginLeft: 'auto',
                  background: '#ef4444',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px',
                  color: 'white',
                }}
              />
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div style={{
                background: '#f0fdf4',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #bbf7d0',
              }}>
                <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>
                  Successfully Registered
                </div>
                <div style={{ color: '#16a34a', fontSize: '28px', fontWeight: 700 }}>
                  {successResults.process_results.success}
                </div>
              </div>
              {successResults.process_results.failed > 0 && (
                <div style={{
                  background: '#fef2f2',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #fca5a5',
                }}>
                  <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '4px' }}>
                    Failed
                  </div>
                  <div style={{ color: '#dc2626', fontSize: '28px', fontWeight: 700 }}>
                    {successResults.process_results.failed}
                  </div>
                </div>
              )}
            </div>

            {successResults.process_results.successful_teachers && successResults.process_results.successful_teachers.length > 0 && (
              <div style={{
                background: '#f9fafb',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #e5e7eb',
                maxHeight: '400px',
                overflowY: 'auto',
              }}>
                <h4 style={{ color: '#2855ae', fontSize: '14px', fontWeight: 600, margin: '0 0 12px 0' }}>
                  Registered Teachers ({successResults.process_results.successful_teachers.length})
                </h4>
                <div style={{ display: 'grid', gap: '8px' }}>
                  {successResults.process_results.successful_teachers.map((teacher, idx) => (
                    <div key={idx} style={{
                      background: 'white',
                      borderRadius: '8px',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr 1.5fr',
                      gap: '12px',
                      fontSize: '13px',
                      color: '#374151',
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, color: '#2855ae' }}>{teacher.name}</div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>{teacher.email}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Teacher ID</div>
                        <div style={{ fontWeight: 600 }}>{teacher.teacher_id}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>User ID</div>
                        <div style={{ fontWeight: 600 }}>{teacher.user_id}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>Credentials</div>
                        <div style={{ fontWeight: 600, fontSize: '11px' }}>
                          {teacher.username} / {teacher.password}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Validation Errors Dialog */}
        <Dialog
          visible={showErrors}
          onHide={() => setShowErrors(false)}
          header={`Validation Errors (${validationErrors.length})`}
          style={{ width: '90vw', maxWidth: '800px' }}
          modal
        >
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {validationErrors.map((err, idx) => (
              <div key={idx} style={{
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
              }}>
                <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: '4px' }}>
                  Row #{err.row}: {err.message}
                </div>
                {err.details && (
                  <ul style={{ margin: '8px 0', paddingLeft: '20px', fontSize: '13px' }}>
                    {err.details.map((d, i) => <li key={i}>{d}</li>)}
                  </ul>
                )}
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                  {err.data.name} | {err.data.email}
                </div>
              </div>
            ))}
          </div>
        </Dialog>
      </div>
    </>
  );
}